/**
 * F32 (Wave 2C) — admin open-games moderation list page.
 *
 * Route: `/open-games`
 * Permission: `open_games:read` (super_admin + support + content_moderator).
 *
 * Chip-row status filter (Open / Full / Closed / Cancelled / All via `?status=`)
 * + a sport <Select> filter (`?sport=`) + a "Reported only" Chip toggle
 * (`?reported=true`) + a free-text SearchInput (client-side title/venue match).
 * Each row links to the detail page where the suspend/unsuspend action lives.
 * Mirrors the F31 skill-disputes + F23 commission-statements queue shape.
 *
 * Admin is suspend/view-only (D20) — there are no list-level write actions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chip,
  EmptyState,
  Pagination,
  SearchInput,
  Select,
  Skeleton,
  Stat,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { OPEN_GAME_STATUS_VALUES, SPORT_NAME_VALUES, type OpenGameStatus } from '@bravemove/types';
import {
  adminOpenGamesApi,
  type AdminOpenGameListItem,
  type SearchGamesOpts,
} from '../../api/openGames';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';
import { usePageKpis } from '../../hooks/usePageKpis';

const PAGE_SIZE = 25;

type UiStatusFilter = OpenGameStatus | 'all';
const DEFAULT_STATUS: UiStatusFilter = 'all';

const STATUS_TABS: ReadonlyArray<UiStatusFilter> = ['all', ...OPEN_GAME_STATUS_VALUES];

/** open_games lifecycle status → StatusChip tone. */
const STATUS_CHIP_MAP: Record<OpenGameStatus, StatusChipStatus> = {
  open: 'approved',
  filled: 'submitted',
  closed: 'pending',
  cancelled: 'rejected',
};

function humaniseSport(sport: string): string {
  return sport.replace(/-/g, ' ');
}

export function AdminOpenGamesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = (searchParams.get('status') as UiStatusFilter | null) ?? DEFAULT_STATUS;
  const sportParam = searchParams.get('sport') ?? '';
  const reportedParam = searchParams.get('reported') === 'true';
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;

  const [rows, setRows] = useState<readonly AdminOpenGameListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // KPI counts — derived from a separate best-effort fetch on mount.
  // F61 (A3) — one unaudited `GET /admin/page-kpis` (was 2–4 audited
  // `perPage=1` list reads); refreshed after any admin action.
  const kpiCounts = usePageKpis('open_games');

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opts: SearchGamesOpts = {
        status: statusParam,
        page: pageParam,
        perPage: PAGE_SIZE,
        ...(sportParam ? { sport: sportParam as NonNullable<SearchGamesOpts['sport']> } : {}),
        ...(reportedParam ? { reported: true } : {}),
      };
      const data = await adminOpenGamesApi.searchGames(opts);
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [statusParam, sportParam, reportedParam, pageParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const setParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      next.delete('page');
      return next;
    });
  };

  const onStatusChange = (value: UiStatusFilter) =>
    setParam('status', value === DEFAULT_STATUS ? null : value);

  const onPageChange = (next: number) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('page', String(next));
      return np;
    });
  };

  // Client-side text match across title + venue (the server list is already
  // filtered by status/sport/reported; search is a within-page refinement).
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => (r.title ?? '').toLowerCase().includes(q) || r.venueName.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const sportOptions = useMemo(
    () => [
      { value: '', label: t('open-game:admin.list.filter_sport', { defaultValue: 'Sport' }) },
      ...SPORT_NAME_VALUES.map((s) => ({ value: s, label: humaniseSport(s) })),
    ],
    [t],
  );

  const columns = useMemo<readonly TableColumn<AdminOpenGameListItem>[]>(
    () => [
      {
        key: 'title',
        header: t('open-game:post.title_label', { defaultValue: 'Title' }),
        cell: (row) => (
          <span className="text-[13px] text-ink line-clamp-1 max-w-[24ch]">
            {row.title ?? row.venueName}
          </span>
        ),
      },
      {
        key: 'sport',
        header: t('open-game:admin.list.filter_sport', { defaultValue: 'Sport' }),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2 capitalize">{humaniseSport(row.sport)}</span>
        ),
      },
      {
        key: 'status',
        header: t('open-game:admin.list.filter_status', { defaultValue: 'Status' }),
        cell: (row) => (
          <div className="flex items-center gap-1.5">
            <StatusChip
              size="sm"
              status={STATUS_CHIP_MAP[row.status]}
              label={t(`open-game:status.${row.status}`)}
            />
            {row.suspendedAt ? (
              <StatusChip
                size="sm"
                status="rejected"
                label={t('open-game:admin.detail.suspended_badge', { defaultValue: 'Suspended' })}
              />
            ) : null}
          </div>
        ),
      },
      {
        key: 'spots',
        header: t('open-game:post.spots_label', { defaultValue: 'Spots' }),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2 font-mono">
            {row.spotsFilled}/{row.spotsTotal}
          </span>
        ),
      },
      {
        key: 'reports',
        header: t('open-game:admin.detail.reports', { defaultValue: 'Reports' }),
        cell: (row) =>
          row.openReportCount > 0 ? (
            <StatusChip size="sm" status="in_review" label={String(row.openReportCount)} />
          ) : (
            <span className="text-[12px] text-ink-3">—</span>
          ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-6" data-testid="open-games-page">
      <PageHeader title={t('open-game:admin.list.title', { defaultValue: 'Open games' })} />

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="open-games-kpi-strip">
        <Stat
          label={t('open-game:admin.kpi.open', { defaultValue: 'Open games' })}
          value={kpiCounts === null ? '—' : kpiCounts.open}
        />
        <Stat
          label={t('open-game:admin.kpi.filled', { defaultValue: 'Filled games' })}
          value={kpiCounts === null ? '—' : kpiCounts.filled}
        />
        <Stat
          label={t('open-game:admin.kpi.suspended', { defaultValue: 'Suspended games' })}
          value={kpiCounts === null ? '—' : kpiCounts.suspended}
        />
        <Stat
          label={t('open-game:admin.kpi.reported', { defaultValue: 'Reported games' })}
          value={kpiCounts === null ? '—' : kpiCounts.reported}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div
          role="tablist"
          aria-label={t('open-game:admin.list.filter_status', { defaultValue: 'Status' })}
          className="flex flex-wrap items-center gap-2"
        >
          {STATUS_TABS.map((tab) => (
            <Chip
              key={tab}
              selected={statusParam === tab}
              onClick={() => onStatusChange(tab)}
              data-testid={`open-games-tab-${tab}`}
            >
              {tab === 'all'
                ? t('admin:nav.all', { defaultValue: 'All' })
                : t(`open-game:status.${tab}`)}
            </Chip>
          ))}
        </div>

        <Chip
          selected={reportedParam}
          onClick={() => setParam('reported', reportedParam ? null : 'true')}
          data-testid="open-games-reported-toggle"
        >
          {t('open-game:admin.list.filter_reported', { defaultValue: 'Reported only' })}
        </Chip>

        <div className="w-44">
          <Select
            size="sm"
            options={sportOptions}
            value={sportParam}
            onChange={(e) => setParam('sport', e.target.value || null)}
            aria-label={t('open-game:admin.list.filter_sport', { defaultValue: 'Sport' })}
            data-testid="open-games-sport-select"
          />
        </div>

        <div className="w-56">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('open-game:admin.list.title', { defaultValue: 'Open games' })}
            data-testid="open-games-search"
          />
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="open-games-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : visibleRows.length === 0 ? (
        <EmptyState
          title={t('open-game:admin.list.empty', { defaultValue: 'No open games match.' })}
        />
      ) : (
        <>
          <Table
            data={visibleRows as AdminOpenGameListItem[]}
            columns={columns as TableColumn<AdminOpenGameListItem>[]}
            rowKey={(row) => row.id}
            onRowPress={(row) => navigate(`/open-games/${row.id}`)}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination page={pageParam} pageCount={pageCount} onChange={onPageChange} />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default AdminOpenGamesPage;
